import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

type ChoiceProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> & {
  label: ReactNode;
  /** An explanation under the sentence; it describes the control instead of
   * becoming part of its name. */
  hint?: ReactNode;
};

/**
 * Checkbox and Radio (CO-171): one row for a yes/no or a pick-one — the
 * native control, its sentence, and optionally a hint under it. Replaces the
 * label patterns that grew apart (`check-row`, `choice`, `checkbox-label`,
 * `field--inline`, `build-form__check`, `onboarding-check`), which aligned the
 * box differently from one screen to the next.
 *
 * The whole row is the click target. The control is named by the sentence
 * alone (`aria-labelledby`) and described by the hint. A radio belongs to a
 * group: give the rows one `name` inside a `fieldset` with a `legend`.
 * Other props go to the `input`.
 */
function Choice({ type, label, hint, className, ...input }: ChoiceProps & { type: 'checkbox' | 'radio' }) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [hintId, input['aria-describedby']].filter(Boolean).join(' ') || undefined;
  return (
    <label className={`check-row${className ? ` ${className}` : ''}`}>
      <input {...input} type={type} aria-labelledby={labelId} aria-describedby={describedBy} />
      <span className="check-row__text">
        <span id={labelId}>{label}</span>
        {hint ? (
          <span id={hintId} className="field__hint check-row__hint">
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function Checkbox(props: ChoiceProps) {
  return <Choice type="checkbox" {...props} />;
}

export function Radio(props: ChoiceProps) {
  return <Choice type="radio" {...props} />;
}
